import {computed, ref} from 'vue';
import {clientTermsAndConditionsApi, legalEntityApi, platformSubsidiaryApi, serviceLevelApiEndPoints} from '@configuration';
import {defineStore, skipHydrate} from 'pinia';
import {useRoute} from 'vue-router';
import {useTranslation, useUniversalPost} from '#imports';
import {useRegle} from '@malt/forms';
import {readonlyState} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import {useLegalEntityFormValidation} from '~/composables/organization-settings/useLegalEntityFormValidation';
import {
    type AllClientTermsAndConditionsResource,
    type BillingContactUpdateRequest,
    type ClientTeamSummary,
    ClientTermsAndConditionsTarget,
    type EInvoicingSettingsUpdateRequest,
    type InvoicingSettingsUpdateRequest,
    type LegalEntityResource,
    type LegalEntitySummaryResource,
    type PaymentSettingsUpdateRequest,
    type PlatformSubsidiary,
    type ProjectTermsAndConditionsResource,
    type QuoteSettingsUpdateRequest,
    type ServiceLevelFeaturesResource,
} from '~/openapi';
import type {LegalEntityForm, LegalEntityFormTab, LegalFormDetails} from '~/types/legalEntity';

export const useLegalEntityStore = defineStore('legal-entity-store', () => {
    const {t} = useTranslation();
    const route = useRoute();
    const legalEntities = ref<LegalEntitySummaryResource[]>([]);
    const platformSubsidiaryList = ref<PlatformSubsidiary[] | null>([]);
    const organizationTeams = ref<ClientTeamSummary[]>([]);
    const legalEntityForm = ref<LegalEntityForm>(initLegalEntityCreationForm());
    const clientTermsAndConditions = ref<AllClientTermsAndConditionsResource | null>(null);
    const projectTermsAndConditions = ref<ProjectTermsAndConditionsResource | null>(null);
    const loading = ref<boolean>(false);
    const serviceLevelFeatures = ref<ServiceLevelFeaturesResource | null>(null);
    const {legalEntityFormRules} = useLegalEntityFormValidation(legalEntityForm, serviceLevelFeatures);
    const {r$} = useRegle(legalEntityForm, legalEntityFormRules);
    const initialFormInvalid = ref<boolean>(r$.details.$error);
    const isLegalInformationFormValid = ref<boolean>(false);
    const isLegalInformationFormDirty = ref<boolean>(false);
    const isArchived = ref<boolean>(false);
    const companyIdentityVerified = ref<boolean>(false);

    const activeTab = ref<LegalEntityFormTab>('legal-entity-entityId-information');
    const currentCreationStep = ref<number>(1);
    const incompleteLegalEntityCount = computed(
        () => legalEntities.value.filter((legalEntity) => legalEntity.missingInformationNotificationCount > 0).length,
    );
    const serviceLevelUpdateLink = computed(() => {
        const redirectUrl = `/client-settings/organization/${legalEntityForm.value.organizationId}`;

        const basePath = `/client-settings/organization/${legalEntityForm.value.organizationId}/service-level?pageSource=blocker&`;

        return (
            `${basePath}` +
            `successRedirectPath=${encodeURIComponent(redirectUrl)}&` +
            `dropRedirectPath=${encodeURIComponent(redirectUrl)}&` +
            `companyId=${route.params.entityId}`
        );
    });

    async function fetchLegalEntities(organizationId: string) {
        try {
            loading.value = true;
            legalEntities.value = await legalEntityApi.findAllSummariesByOrganizationId({organizationId});
        } catch {
            pushVJoySnackbar({props: {message: t('generic.technical.error'), level: 'error'}});
        } finally {
            loading.value = false;
        }
    }

    async function fetchLegalEntityDetails(id: string) {
        try {
            const response = await legalEntityApi.findDetailsById({legalEntityId: id});
            legalEntityForm.value.details = response as LegalFormDetails;
            legalEntityForm.value.eInvoiceRouting = response.eInvoiceRouting ?? {};
            legalEntityForm.value.organizationId = response.organizationId;
            isLegalInformationFormValid.value = response.hasMandatoryInformation;
            isArchived.value = response.isArchived ?? false;
            companyIdentityVerified.value = response.isIdentified;
        } catch {
            pushVJoySnackbar({props: {message: t('generic.technical.error'), level: 'error'}});
        }
    }

    async function fetchLegalEntityPreferences(id: string) {
        try {
            legalEntityForm.value.preferences = await legalEntityApi.findPreferencesById({legalEntityId: id});
            if (!legalEntityForm.value.preferences.clientTerms) {
                const currentFreelancerTerms = (clientTermsAndConditions.value?.currentTermsAndConditions ?? []).find(
                    (t) => t.target === ClientTermsAndConditionsTarget.Freelancer,
                );
                legalEntityForm.value.preferences.clientTerms = currentFreelancerTerms
                    ? ({
                          name: currentFreelancerTerms.fileName,
                          lastModified: currentFreelancerTerms.validityStartDate?.getTime(),
                      } as File)
                    : undefined;
            }
        } catch {
            pushVJoySnackbar({props: {message: t('generic.technical.error'), level: 'error'}});
        }
    }

    async function updateLegalEntityDetails(legalEntityId: string) {
        await legalEntityApi.updateDetails({
            legalEntityId,
            billingContactUpdateRequest: legalEntityForm.value.details.billingContact as BillingContactUpdateRequest,
        });
    }

    async function updateEInvoiceRouting(legalEntityId: string) {
        const {identifierType, routingSuffix} = legalEntityForm.value.eInvoiceRouting;
        // A routing row is only ever persisted with a concrete identifier type; a blank selection means
        // "not configured" and must not be sent (the UI forces a selection before the section is editable).
        if (!identifierType) {
            return;
        }
        await legalEntityApi.updateEInvoiceRouting({
            legalEntityId,
            eInvoiceRouting: {identifierType, routingSuffix},
        });
    }

    async function updateLegalEntityPreferences(legalEntityId: string) {
        await legalEntityApi.updatePreferences({
            legalEntityId,
            legalEntityPreferencesRequest: {
                invoicingSettings: legalEntityForm.value.preferences.invoicingSettings as InvoicingSettingsUpdateRequest,
                paymentSettings: legalEntityForm.value.preferences.paymentSettings as PaymentSettingsUpdateRequest,
                quoteSettings: legalEntityForm.value.preferences.quoteSettings as QuoteSettingsUpdateRequest,
                eInvoicingSettings: legalEntityForm.value.preferences.eInvoicingSettings as EInvoicingSettingsUpdateRequest,
            },
        });
    }

    async function fetchPlatformSubsidiaryList() {
        platformSubsidiaryList.value = (await platformSubsidiaryApi.findAll()).subsidiaries ?? [];
    }

    async function fetchClientTeams(entityId: string) {
        const {clientTeams} = await legalEntityApi.findAllClientTeams({legalEntityId: entityId});
        organizationTeams.value = clientTeams;
    }

    async function createLegalEntity(request: LegalEntityResource) {
        try {
            await legalEntityApi.create({legalEntityRequest: request});
            pushVJoySnackbar({props: {message: t('general.form.success'), level: 'success'}});
        } catch {
            pushVJoySnackbar({props: {message: t('generic.technical.error'), level: 'error'}});
        }
    }

    async function fetchClientTermsAndConditions(legalEntityId: string) {
        try {
            clientTermsAndConditions.value = await clientTermsAndConditionsApi.findAllClientTermsAndConditions({legalEntityId});
            const currentTerms = clientTermsAndConditions.value?.currentTermsAndConditions ?? [];
            const currentFreelancerTerms = currentTerms.find((t) => t.target === ClientTermsAndConditionsTarget.Freelancer) ?? null;
            const currentSubcontractorTerms = currentTerms.find((t) => t.target === ClientTermsAndConditionsTarget.Subcontractor) ?? null;
            legalEntityForm.value.preferences.clientTerms = currentFreelancerTerms
                ? ({
                      name: currentFreelancerTerms.fileName,
                      lastModified: currentFreelancerTerms.validityStartDate?.getTime(),
                  } as File)
                : null;
            legalEntityForm.value.preferences.clientTermsSubcontractor = currentSubcontractorTerms
                ? ({
                      name: currentSubcontractorTerms.fileName,
                      lastModified: currentSubcontractorTerms.validityStartDate?.getTime(),
                  } as File)
                : null;
        } catch {
            pushVJoySnackbar({props: {message: t('generic.technical.error'), level: 'error'}});
        }
    }

    async function fetchProjectTermsAndConditions() {
        try {
            projectTermsAndConditions.value = await legalEntityApi.getLastProjectTermsAndConditions();
        } catch {
            pushVJoySnackbar({props: {message: t('generic.technical.error'), level: 'error'}});
        }
    }

    async function uploadClientTermsAndConditionsDocument(legalEntityId: string, file: File) {
        await clientTermsAndConditionsApi.uploadClientTermsAndConditionsDocument({legalEntityId, file});
        clientTermsAndConditions.value = await clientTermsAndConditionsApi.findAllClientTermsAndConditions({legalEntityId});
    }

    async function uploadSubcontractorClientTermsAndConditionsDocument(legalEntityId: string, file: File) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('target', ClientTermsAndConditionsTarget.Subcontractor);
        await useUniversalPost(`/client-settings/api/legal-entities/${legalEntityId}/terms-and-conditions`, {
            method: 'POST',
            body: formData,
        });
        clientTermsAndConditions.value = await clientTermsAndConditionsApi.findAllClientTermsAndConditions({legalEntityId});
    }

    async function uploadClientTermsAndConditionsDocumentForWholeOrganization(organizationId: string, file: File) {
        return await clientTermsAndConditionsApi.uploadClientTermsAndConditionsDocumentForWholeOrganization({organizationId, file});
    }

    async function fetchServiceLevelFeatures(organizationId: string) {
        serviceLevelFeatures.value = await serviceLevelApiEndPoints.getServiceLevelFeatures({organizationId});
    }

    function initLegalEntityCreationForm(): LegalEntityForm {
        return {
            details: {
                billingContact: {
                    email: '',
                    isSeparateEmailsForInvoices: false,
                    extraEmails: new Set(),
                },
                legalInformation: {
                    address: {
                        street: '',
                        city: '',
                        zipCode: '',
                        countryCode: 'FR',
                        region: '',
                    },
                    legalName: '',
                    registrationNumber: '',
                    vatIdentificationNumber: '',
                },
                invoicingSettings: {
                    invoicingMode: 'DIRECT',
                    extraInformation: '',
                },
                eInvoiceRoutingApplicable: false,
            },
            eInvoiceRouting: {},
            affiliation: {
                clientTeams: new Set(),
            },
            preferences: {
                paymentSettings: {
                    isInvoiceBeforePayment: false,
                    paymentTermInDays: 30,
                    platformSubsidiaryId: '',
                    contractReference: '',
                    applyEndOfMonthRule: false,
                },
                invoicingSettings: {
                    invoicingMode: 'DIRECT',
                    extraInformation: '',
                },
                quoteSettings: {
                    showFeesOnQuote: false,
                },
                eInvoicingSettings: {
                    excludedFromAutomaticEInvoicing: false,
                },
            },
            organizationId: '',
        };
    }

    return {
        fetchLegalEntities,
        fetchLegalEntityDetails,
        fetchLegalEntityPreferences,
        fetchPlatformSubsidiaryList,
        updateLegalEntityPreferences,
        updateLegalEntityDetails,
        updateEInvoiceRouting,
        createLegalEntity,
        fetchClientTeams,
        fetchClientTermsAndConditions,
        fetchProjectTermsAndConditions,
        uploadClientTermsAndConditionsDocument,
        uploadSubcontractorClientTermsAndConditionsDocument,
        uploadClientTermsAndConditionsDocumentForWholeOrganization,
        initLegalEntityCreationForm,
        fetchServiceLevelFeatures,
        legalEntities: readonlyState(legalEntities),
        platformSubsidiaryList: readonlyState(platformSubsidiaryList),
        organizationTeams: readonlyState(organizationTeams),
        clientTermsAndConditions: readonlyState(clientTermsAndConditions),
        projectTermsAndConditions: projectTermsAndConditions,
        serviceLevelCompanyFeatures: readonlyState(serviceLevelFeatures),
        serviceLevelUpdateLink: readonlyState(serviceLevelUpdateLink),
        isLegalInformationFormValid,
        isLegalInformationFormDirty,
        isArchived,
        companyIdentityVerified,
        activeTab,
        currentCreationStep,
        legalEntityForm,
        initialFormInvalid,
        incompleteLegalEntityCount,
        r$: skipHydrate(r$),
        loading,
    };
});
